import { Businesses, Routes } from "@just-in-types";
import { NavigateFn } from "@reach/router";
import * as React from "react";
import { connect } from "react-redux";
import generateManifest from "./generateManifest";
import {
  SetActiveSite,
  setActiveSite,
  SetAllowOptOut,
  setAllowOptOut,
  setBusinessPath,
  SetBusinessPath,
  SetGuid,
  setGuid,
  setOptInField,
  SetRootPath,
  setRootPath,
  SetRouteComponents,
  setRouteComponents,
  SetShowCoupons,
  setShowCoupons,
  setTheme,
  SetTheme,
  updateKioskCredentials,
} from "./store/actions";
import { IState } from "./store/reducers";
import CouponGoal from "./views/CouponGoal";
import Coupons from "./views/Coupons";
import CovidQuestions from "./views/CovidQuestions";
import CustomerType from "./views/CustomerType";
import DateOfBirth from "./views/DateOfBirth";
import Email from "./views/Email";
import Language from "./views/Language";
import Mobile from "./views/Mobile";
import Name from "./views/Name";
import OptIn from "./views/OptIn";
import Scanner from "./views/Scanner";
import Signature from "./views/Signature";
import Supercard from "./views/Supercard";
import Thanks from "./views/Thanks";
import Vehicles from "./views/Vehicles";
import Zip from "./views/Zip";

export interface RouteComponentProps {
  businessId: Businesses.Business["businessId"];
  default: RouteComponent["default"];
  site: Businesses.Site;
  siteId: Businesses.Site["siteId"];
  navigate: NavigateFn;
  path: RouteComponent["path"];
  routes: Businesses.Site["routes"];
  takeMugshot?: boolean;
  waiverText?: string;
}

export interface RouteComponent {
  alwaysShow?: boolean;
  component?: React.ComponentClass<RouteComponentProps>;
  default?: boolean;
  enabled?: boolean;
  path: Routes.RouteString;
  takeMugshot?: boolean;
  waiverText?: string;
}

interface ISiteDispatchProps {
  onSetAllowOptOut: SetAllowOptOut;
  onSetBusinessPath: SetBusinessPath;
  onSetGuid: SetGuid;
  onSetOptInField: any;
  onSetRootPath: SetRootPath;
  onSetRouteComponents: SetRouteComponents;
  onSetShowCoupons: SetShowCoupons;
  onSetActiveSite: SetActiveSite;
  onSetTheme: SetTheme;
}

interface ISiteOwnProps {
  business: Businesses.Business;
  default?: boolean;
  path: string;
  site: Businesses.Site;
}

interface ISiteStateProps {
  routes: null | Routes.RoutesObject;
  routeStrings: Routes.RouteString[];
}

type ISiteProps = ISiteDispatchProps & ISiteOwnProps & ISiteStateProps;

class Site extends React.Component<ISiteProps> {
  public componentDidMount() {
    // TODO: move this crap into actions
    const {
      business: { businessId, guid: businessGuid, path: businessPath, theme: businessTheme },
      // TODO: bake all the actions into one, passing the whole site
      // or business into the argument.
      site,
      site: { allowOptOut, guid: siteGuid, path: sitePath, optInField, routes, showCoupons, siteId, theme: siteTheme },
      onSetAllowOptOut,
      onSetBusinessPath,
      onSetGuid,
      onSetOptInField,
      onSetRootPath,
      onSetRouteComponents,
      onSetShowCoupons,
      onSetActiveSite,
      onSetTheme,
    } = this.props;

    generateManifest(`${businessPath}/${sitePath}`);
    updateKioskCredentials({ businessId, businessPath, siteId, sitePath });

    if (siteTheme) {
      onSetTheme(siteTheme);
    } else {
      if (businessTheme) {
        onSetTheme(businessTheme);
      }
    }

    if (typeof allowOptOut === "boolean") {
      onSetAllowOptOut(allowOptOut);
    }

    onSetBusinessPath(businessPath);
    onSetGuid({ guid: siteGuid ? siteGuid : businessGuid });
    onSetRouteComponents(businessId, routes);
    onSetRootPath(`/${businessPath}/${sitePath}`);
    onSetShowCoupons(showCoupons);
    onSetActiveSite(site);
    onSetOptInField(optInField);
  }

  public render() {
    const components = this.routeBuilder();

    const children = this.props.children as any;

    return components ? children.props.children(components) : [];
  }

  private routeBuilder = () => {
    const { routes } = this.props;

    if (routes) {
      return this.routeObjectBuilder(routes);
    }
  };

  private routeObjectBuilder = (routesObject: Routes.RoutesObject) => {
    const routeComponents: RouteComponent[] = [
      {
        component: Mobile,
        default: true,
        path: "mobile",
      },
    ];

    if (process.env.NODE_ENV === "development") {
      routeComponents.push({ component: DateOfBirth, path: "dateofbirth" });
    }

    for (const path in routesObject) {
      if (path) {
        const routePath = path as Routes.RouteString;
        const route = routesObject[routePath] as Routes.SignatureRoute;

        if (
          path === "email" ||
          path === "language" ||
          path === "name" ||
          path === "signature" ||
          path === "vehicles" ||
          path === "zip" ||
          path === "dateofbirth" ||
          path === "receiptCode" ||
          path === "optin" ||
          path === "customertype" ||
          path === "supercard" ||
          path === "coupongoal" ||
          path === "covidquestions"
        ) {
          const routeComponent: {
            component: undefined | React.ComponentClass<RouteComponentProps>;
            path: Routes.RouteString;
            takeMugshot?: boolean;
            waiverText?: string;
          } = { component: undefined, path };

          routeComponent.component =
            path === "coupongoal"
              ? CouponGoal
              : path === "dateofbirth"
              ? DateOfBirth
              : path === "email"
              ? Email
              : path === "language"
              ? Language
              : path === "name"
              ? Name
              : path === "signature"
              ? Signature
              : path === "vehicles"
              ? Vehicles
              : path === "zip"
              ? Zip
              : path === "receiptCode"
              ? Scanner
              : path === "optin"
              ? OptIn
              : path === "customertype"
              ? CustomerType
              : path === "supercard"
              ? Supercard
              : path === "covidquestions"
              ? CovidQuestions
              : undefined;

          if (path === "signature" && typeof route.takeMugshot === "boolean" && route.waiverText) {
            routeComponent.takeMugshot = route.takeMugshot;
            routeComponent.waiverText = route.waiverText;
          }

          if (routeComponent.component) {
            routeComponent.path = path;

            routeComponents.push(routeComponent);
          }
        }
      }
    }

    routeComponents.push(
      {
        component: Thanks,
        path: "thanks",
      },
      { component: Email, path: "email" },
      { component: Coupons, path: "coupons" }
    );

    return routeComponents;
  };
}

const mapStateToProps = ({ router: { routes, routeStrings } }: IState) => ({
  routeStrings,
  routes,
});

const mapDispatchToProps = {
  onSetActiveSite: setActiveSite,
  onSetAllowOptOut: setAllowOptOut,
  onSetBusinessPath: setBusinessPath,
  onSetGuid: setGuid,
  onSetOptInField: setOptInField,
  onSetRootPath: setRootPath,
  onSetRouteComponents: setRouteComponents,
  onSetShowCoupons: setShowCoupons,
  onSetTheme: setTheme,
};

export default connect<ISiteStateProps, ISiteDispatchProps, ISiteOwnProps>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps
)(Site);



// WEBPACK FOOTER //
// ./src/Site.tsx